/**
 * `/app/admin/users` — admin user management.
 *
 * Single page. Three building blocks composed here:
 *
 *   FilterBar    — status tabs (with counts) + email search
 *   UsersTable   — the actual table, scrolls on overflow
 *   Pagination   — page selector at the bottom
 *
 * Actions (approve / reject / suspend / reactivate) trigger a confirm
 * dialog before firing the mutation. Mutations invalidate the list
 * query so the table reflects the new state.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle2, XCircle, Pause, Play, MoreHorizontal, KeyRound, X,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { timeAgo } from '@/lib/format'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth'
import {
  useAdminUsers, useUserStatusCounts,
  useApproveUser, useRejectUser, useSuspendUser, useReactivateUser,
  useUpdateAccountLabel, useAdminResetPassword, useAdminBybitKeys,
  type AdminUser, type AdminBybitKey, type UserStatus,
} from '@/hooks/useAdminUsersApi'

type FilterKey = UserStatus | 'all'

interface PendingAction {
  type: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'reset_password'
  user: AdminUser
}

export default function AdminUsers() {
  const { t } = useTranslation('app')
  const currentUserId = useAuthStore((s) => s.user?.id)

  const [filter, setFilter]   = useState<FilterKey>('pending')
  const [search, setSearch]   = useState('')
  const [page, setPage]       = useState(1)
  const pageSize              = 20

  const list   = useAdminUsers({
    status: filter === 'all' ? null : filter,
    q: search, page, pageSize,
  })
  const counts = useUserStatusCounts()

  const [pending, setPending] = useState<PendingAction | null>(null)
  // PULSE-269: временный пароль после админского сброса (показ один раз)
  const [tempPw, setTempPw] = useState<{ email: string; password: string } | null>(null)
  const resetPw = useAdminResetPassword()

  const approve    = useApproveUser({ onSuccess: () => setPending(null) })
  const reject     = useRejectUser({ onSuccess: () => setPending(null) })
  const suspend    = useSuspendUser({ onSuccess: () => setPending(null) })
  const reactivate = useReactivateUser({ onSuccess: () => setPending(null) })

  const actionMap = useMemo(() => ({
    approve, reject, suspend, reactivate,
  }), [approve, reject, suspend, reactivate])

  function handleConfirm() {
    if (!pending) return
    if (pending.type === 'reset_password') {
      const email = pending.user.email ?? ''
      resetPw.mutate({ id: pending.user.id }, {
        onSuccess: (d) => {
          setPending(null)
          setTempPw({ email, password: d.temp_password })
        },
      })
      return
    }
    actionMap[pending.type].mutate({ id: pending.user.id })
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('admin.users.title')}
        </h1>
        <p className="mt-1 text-sm text-textDim">
          {t('admin.users.subtitle')}
        </p>
      </header>

      <FilterBar
        filter={filter}
        onFilter={(f) => { setFilter(f); setPage(1) }}
        search={search}
        onSearch={(s) => { setSearch(s); setPage(1) }}
        counts={counts.data}
      />

      <UsersTable
        items={list.data?.items}
        loading={list.isLoading}
        currentUserId={currentUserId ?? null}
        onAction={setPending}
      />

      {list.data && list.data.total > pageSize && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={list.data.total}
          onPage={setPage}
        />
      )}

      {pending && (
        <ConfirmDialog
          open
          title={t(`admin.users.actions.${pending.type}`)}
          description={
            t(`admin.users.confirm.${pending.type}`, { email: pending.user.email })
          }
          confirmLabel={t(`admin.users.actions.${pending.type}`)}
          variant={pending.type === 'reject' || pending.type === 'suspend'
                    ? 'danger' : 'primary'}
          loading={pending.type === 'reset_password'
                    ? resetPw.isPending
                    : actionMap[pending.type].isPending}
          onConfirm={handleConfirm}
          onCancel={() => setPending(null)}
        />
      )}

      {tempPw && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4"
          onClick={() => setTempPw(null)}
        >
          <div
            className="bg-ink border border-line w-full max-w-md mt-24 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">{t('admin.users.reset_dialog.title')}</h2>
              <button onClick={() => setTempPw(null)} className="text-textFaint hover:text-textDim">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-textDim mb-3">
              {tempPw.email} — {t('admin.users.reset_dialog.body')}
            </p>
            <div className="font-mono text-lg bg-surface border border-line px-3 py-2 select-all text-center">
              {tempPw.password}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Filter bar ──────────────────────────────────────────────────────

function FilterBar({
  filter, onFilter, search, onSearch, counts,
}: {
  filter: FilterKey
  onFilter(f: FilterKey): void
  search: string
  onSearch(s: string): void
  counts: { pending: number; active: number; suspended: number; rejected: number } | undefined
}) {
  const { t } = useTranslation('app')
  const tabs: { key: FilterKey; count?: number }[] = [
    { key: 'pending',   count: counts?.pending },
    { key: 'active',    count: counts?.active },
    { key: 'suspended', count: counts?.suspended },
    { key: 'rejected',  count: counts?.rejected },
    { key: 'all'                                 },
  ]
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4">
      <nav className="flex border border-line bg-surface rounded overflow-x-auto divide-x divide-line">
        {tabs.map(({ key, count }) => (
          <button
            key={key}
            onClick={() => onFilter(key)}
            className={cn(
              'px-3 py-1.5 text-xs uppercase tracking-wider transition-colors',
              filter === key
                ? 'bg-fire-faint text-fire'
                : 'text-textDim hover:text-text hover:bg-elevated',
            )}
          >
            <span>{t(`admin.users.filters.${key}`)}</span>
            {typeof count === 'number' && (
              <span className="ml-1.5 font-mono text-textFaint">{count}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="sm:max-w-xs flex-1">
        <Input
          type="search"
          placeholder={t('admin.users.search_placeholder')}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
    </div>
  )
}

// ─── Users table ─────────────────────────────────────────────────────

function UsersTable({
  items, loading, currentUserId, onAction,
}: {
  items: AdminUser[] | undefined
  loading: boolean
  currentUserId: number | null
  onAction(a: PendingAction): void
}) {
  const { t } = useTranslation('app')
  const { data: keys } = useAdminBybitKeys()
  const keyById = useMemo(
    () => new Map((keys ?? []).map((k) => [k.user_id, k])),
    [keys],
  )

  if (loading) {
    return (
      <div className="bg-surface border border-line rounded p-8 text-center text-sm text-textDim">
        {t('admin.users.table.loading')}
      </div>
    )
  }
  if (!items || items.length === 0) {
    return (
      <div className="bg-surface border border-line rounded p-8 text-center text-sm text-textDim">
        {t('admin.users.table.empty')}
      </div>
    )
  }

  return (
    <div className="bg-surface border border-line rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-elevated text-textFaint text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left  px-4 py-3 font-medium">{t('admin.users.table.email')}</th>
            <th className="text-left  py-3 font-medium">{t('admin.users.table.role')}</th>
            <th className="text-left  py-3 font-medium">{t('admin.users.table.status')}</th>
            <th className="text-left  py-3 font-medium">{t('admin.users.table.created')}</th>
            <th className="text-left  py-3 font-medium">{t('admin.users.table.last_login')}</th>
            <th className="text-right px-4 py-3 font-medium">{t('admin.users.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {items.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              bybitKey={keyById.get(u.id)}
              isSelf={currentUserId === u.id}
              onAction={onAction}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function UserRow({
  user, bybitKey, isSelf, onAction,
}: {
  user: AdminUser
  bybitKey: AdminBybitKey | undefined
  isSelf: boolean
  onAction(a: PendingAction): void
}) {
  const { t } = useTranslation('app')
  const updateLabel = useUpdateAccountLabel()

  // Sub-accounts get an inline-editable label cell instead of an email.
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<string>(user.account_label ?? '')

  const isSub = user.master_user_id != null

  const saveLabel = async () => {
    setEditing(false)
    const next = draft.trim()
    if (next === (user.account_label ?? '')) return
    try {
      await updateLabel.mutateAsync({ id: user.id, label: next || null })
    } catch { /* toast via interceptor */ }
  }

  return (
    <tr className="hover:bg-elevated/40 transition-colors">
      <td className="px-4 py-3">
        {isSub ? (
          editing ? (
            <input
              autoFocus
              className="text-sm bg-elevated border border-fire rounded px-2 py-0.5 w-40"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={saveLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                if (e.key === 'Escape') {
                  setDraft(user.account_label ?? ''); setEditing(false)
                }
              }}
              maxLength={40}
            />
          ) : (
            <button
              type="button"
              onClick={() => { setDraft(user.account_label ?? ''); setEditing(true) }}
              className="text-left group"
              title={t('admin.users.edit_label') as string}
            >
              <span className="font-medium text-textDim group-hover:text-text">
                {user.account_label
                  ? `«${user.account_label}»`
                  : <em className="text-textFaint">{t('admin.users.no_label')}</em>}
              </span>
            </button>
          )
        ) : (
          <div className="font-medium">{user.email ?? '—'}</div>
        )}
        <div className="text-xs text-textFaint font-mono mt-0.5">
          id {user.id}
          {isSub && (
            <> · master {user.master_user_id}</>
          )}
        </div>
      </td>
      <td className="py-3">
        <span className="text-xs uppercase tracking-wider">
          {isSub ? (
            <span className="text-amber">SUB · {user.role}</span>
          ) : user.is_master ? (
            <span className="text-fire">MASTER · {user.role}</span>
          ) : (
            <span className="text-textDim">{user.role}</span>
          )}
        </span>
      </td>
      <td className="py-3">
        <StatusBadge status={user.status} />
        {bybitKey && <BybitKeyBadge k={bybitKey} />}
      </td>
      <td className="py-3 text-xs text-textDim font-mono whitespace-nowrap">
        {timeAgo(user.created_at)}
      </td>
      <td className="py-3 text-xs text-textDim font-mono whitespace-nowrap">
        {user.last_login_at ? timeAgo(user.last_login_at) : '—'}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {isSelf ? (
          <span className="text-xs text-textFaint" title={t('admin.users.self_lock') as string}>
            <MoreHorizontal size={16} className="inline" />
          </span>
        ) : (
          <RowActions user={user} onAction={onAction} />
        )}
      </td>
    </tr>
  )
}

// PULSE-417: Bybit key state under the status badge (only when it needs attention
// or to show the expiry date of a healthy key).
function BybitKeyBadge({ k }: { k: AdminBybitKey }) {
  const { t } = useTranslation('app')
  const date = k.key_expires_at ? new Date(k.key_expires_at).toLocaleDateString() : null
  let cls = 'text-textFaint'
  let label: string
  if (k.severity === 'red') {
    cls = 'text-rose'
    label = t('bybit.admin.badge_expired')
  } else if (k.severity === 'yellow') {
    cls = 'text-amber'
    label = t('bybit.admin.badge_expiring', { days: Math.max(0, Math.floor(k.days_left ?? 0)) })
  } else if (k.severity === 'pending') {
    label = t('bybit.admin.badge_pending')
  } else {
    label = date ? t('bybit.admin.badge_ok', { date }) : t('bybit.admin.badge_ok_nodate')
  }
  return (
    <div className={cn('text-xs font-mono mt-1', cls)} title={k.last_check_error ?? undefined}>
      {label}
    </div>
  )
}

function RowActions({
  user, onAction,
}: { user: AdminUser; onAction(a: PendingAction): void }) {
  const { t } = useTranslation('app')

  // PULSE-269: сброс пароля — только master-аккаунты (у сабов нет логина)
  const resetBtn = user.is_master && user.status !== 'rejected' ? (
    <ActionBtn
      icon={<KeyRound size={13} />}
      variant="ghost"
      onClick={() => onAction({ type: 'reset_password', user })}
    >
      {t('admin.users.actions.reset_password')}
    </ActionBtn>
  ) : null

  if (user.status === 'pending') {
    return (
      <div className="inline-flex gap-1.5">
        <ActionBtn
          icon={<CheckCircle2 size={13} />}
          variant="primary"
          onClick={() => onAction({ type: 'approve', user })}
        >
          {t('admin.users.actions.approve')}
        </ActionBtn>
        <ActionBtn
          icon={<XCircle size={13} />}
          variant="ghost"
          onClick={() => onAction({ type: 'reject', user })}
        >
          {t('admin.users.actions.reject')}
        </ActionBtn>
        {resetBtn}
      </div>
    )
  }
  if (user.status === 'active') {
    return (
      <div className="inline-flex gap-1.5">
        <ActionBtn
          icon={<Pause size={13} />}
          variant="ghost"
          onClick={() => onAction({ type: 'suspend', user })}
        >
          {t('admin.users.actions.suspend')}
        </ActionBtn>
        {resetBtn}
      </div>
    )
  }
  if (user.status === 'suspended') {
    return (
      <div className="inline-flex gap-1.5">
        <ActionBtn
          icon={<Play size={13} />}
          variant="primary"
          onClick={() => onAction({ type: 'reactivate', user })}
        >
          {t('admin.users.actions.reactivate')}
        </ActionBtn>
        {resetBtn}
      </div>
    )
  }
  // rejected — terminal, no actions
  return <span className="text-xs text-textFaint">—</span>
}

function ActionBtn({
  icon, variant, children, onClick,
}: {
  icon: React.ReactNode
  variant: 'primary' | 'ghost'
  children: React.ReactNode
  onClick(): void
}) {
  return (
    <Button size="sm" variant={variant} onClick={onClick}>
      {icon}
      {children}
    </Button>
  )
}

// ─── Pagination ──────────────────────────────────────────────────────

function Pagination({
  page, pageSize, total, onPage,
}: { page: number; pageSize: number; total: number; onPage(p: number): void }) {
  const { t } = useTranslation('app')
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const from = (page - 1) * pageSize + 1
  const to   = Math.min(page * pageSize, total)
  return (
    <div className="mt-4 flex items-center text-xs text-textDim">
      <span className="font-mono">
        {t('admin.users.pagination.showing', { from, to, total })}
      </span>
      <div className="ml-auto flex gap-2">
        <Button
          size="sm" variant="secondary"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t('admin.users.pagination.prev')}
        </Button>
        <Button
          size="sm" variant="secondary"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          {t('admin.users.pagination.next')}
        </Button>
      </div>
    </div>
  )
}
